import { useState, useCallback } from 'react';
import { IconButton, makeStyles, Box } from '@material-ui/core';
import {
  useSidebarPinState,
  useSidebarOpenState,
} from '@backstage/core-components';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { rootTranslationRef } from '../../translations/features/root/root';
import { SidebarMenuIcon } from './SidebarMenuIcon';
import { colors } from '../../theme/theme';
import { ASSETS } from '../../constants';
import { useDeviceDetection } from '../../hooks';

const useStyles = makeStyles(theme => ({
  openSidebarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    height: '48px',
    width: '100%',
  },
  openSidebarLogo: {
    height: '20px',
    width: 'auto',
    marginLeft: '10px',
  },
  openSidebarButton: {
    padding: '8px',
    color: colors.white,
    '&:hover': {
      backgroundColor: colors.whiteAlpha10,
    },
    '&:focus-visible': {
      outline: `2px solid ${colors.white}`,
      outlineOffset: '2px',
    },
  },
  closedSidebarContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    height: '48px',
    width: '100%',
  },
  closedSidebarButton: {
    padding: '22px 32px',
    borderRadius: '2px',
    color: colors.white,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // Em telas < 960px, usa cursor de resize para indicar que pode expandir
    [theme.breakpoints.down('md')]: {
      cursor: 'ew-resize',
      '&:hover': {
        backgroundColor: colors.primaryDark,
      },
    },
    '&:focus-visible': {
      outline: `2px solid ${colors.white}`,
      outlineOffset: '2px',
    },
  },
  closedSidebarLogo: {
    height: '20px',
    width: 'auto',
    objectFit: 'contain',
  },
  iconWrapper: {
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const SidebarToggleButton = () => {
  const { t } = useTranslationRef(rootTranslationRef);
  const { isPinned, toggleSidebarPinState } = useSidebarPinState();
  const { isOpen } = useSidebarOpenState();
  const classes = useStyles();
  const { isSmallScreen } = useDeviceDetection();
  const [isHovered, setIsHovered] = useState(false);

  const handleToggle = useCallback(() => {
    toggleSidebarPinState();
    // Reseta hover após toggle para garantir que logo seja exibida ao fechar
    // Evita estado inconsistente onde ícone poderia permanecer visível
    setIsHovered(false);
  }, [toggleSidebarPinState]);

  if (isOpen) {
    return (
      <Box className={classes.openSidebarContainer}>
        <img
          src={ASSETS.logos.full}
          alt="X-Via Logo"
          className={classes.openSidebarLogo}
        />
        <IconButton
          onClick={handleToggle}
          className={classes.openSidebarButton}
          aria-label={
            isPinned ? t('sidebar.toggle.unpin') : t('sidebar.toggle.pin')
          }
          aria-pressed={isPinned}
          role="button"
          tabIndex={0}
        >
          <SidebarMenuIcon fontSize="small" aria-hidden="true" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box className={classes.closedSidebarContainer}>
      <IconButton
        onClick={handleToggle}
        className={classes.closedSidebarButton}
        aria-label={t('sidebar.toggle.pin')}
        aria-pressed={isPinned}
        role="button"
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Box className={classes.iconWrapper}>
          {!isSmallScreen && (
            <img
              src={ASSETS.logos.icon}
              alt="X-Via Logo"
              className={classes.closedSidebarLogo}
            />
          )}
          {isSmallScreen && !isHovered && (
            <img
              src={ASSETS.logos.icon}
              alt="X-Via Logo"
              className={classes.closedSidebarLogo}
            />
          )}
          {isSmallScreen && isHovered && <SidebarMenuIcon fontSize="small" />}
        </Box>
      </IconButton>
    </Box>
  );
};
